import { animate, state, style, transition, trigger } from '@angular/animations'
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, signal } from '@angular/core'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { ActivatedRoute, Router } from '@angular/router'

import { Lasku } from '@lemon/core-firebase/model/lasku'
import { LaskuToistoTyyppi, ToistuvaLasku } from '@lemon/core-firebase/model/lasku-toistuva'
import { LaskuLahetyksenToistoService } from '@lemon/core-firebase/service/lasku/lasku-lahetyksen-toisto.service'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { DateService } from '@lemon/core/service/date.service'

import { LemonTranslationService } from 'app/_jaettu-angular/service/lemon-translation.service'
import { Observable, Subject } from 'rxjs'
import { map, takeUntil } from 'rxjs/operators'
import { AsiakasService } from '../_angular/service/asiakas/asiakas.service'
import { DebugService } from '../_angular/service/debug.service'
import { FirebaseLemonaid } from '../_angular/service/firebase-lemonator.service'
import { KirjautunutKayttajaService } from '../_angular/service/kirjautunut-kayttaja.service'
import { Asiakas } from '../_jaettu-lemonator/model/asiakas'

interface ToistuvaLaskuRivi extends ToistuvaLasku {
  avain: string
  asiakasNimi: string
  summa: number
  seuraavaLahetysPvmDate: Date
  lahetysFrekvenssi: string
  ensimmainenLaskuPvm: Date
  viimeinenLaskuPvm: Date | null
  yhteensaLahetyksia: number | null
  lahetetytLaskut?: Array<Pick<Lasku, 'avain' | 'p' | 'erap' | 'summa' | 'nro'>>
  ladataanLahetettyjaTietoja?: boolean
}

@Component({
  selector: 'app-toistuvat-laskut-selaa',
  templateUrl: './toistuvat-laskut-selaa.component.html',
  styleUrls: ['./toistuvat-laskut-selaa.component.css'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class ToistuvatLaskutSelaaComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) set paginator(paginator: MatPaginator) {
    this.dataSource.paginator = paginator
  }
  @ViewChild(MatSort) set sort(sort: MatSort) {
    this.dataSource.sort = sort
  }

  private _ngUnsubscribe = new Subject<void>()

  dataSource = new MatTableDataSource<ToistuvaLaskuRivi>([])
  displayedColumns: string[] = ['expand', 'asiakasNimi', 'lahetysFrekvenssi', 'ensimmainenLaskuPvm', 'viimeinenLaskuPvm', 'lahetyksia', 'summa']
  expandedElement: ToistuvaLaskuRivi | null = null
  readonly lataa = signal(true)
  asiakas: Asiakas

  kirjanpitajaOnDevaajaObservable: Observable<boolean>
  toistuvatLaskutUriObservable: Observable<string>
  toistuvatLaskutEncodedUriObservable: Observable<string>

  constructor(
    private _firebaseLemonaid: FirebaseLemonaid,
    private _dateService: DateService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _laskuUriService: LaskuUriService,
    private _translationService: LemonTranslationService,
    private _laskuLahetyksenToistoService: LaskuLahetyksenToistoService,
    private _debugService: DebugService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _asiakasService: AsiakasService,
    private _changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.kirjanpitajaOnDevaajaObservable = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable
    this.toistuvatLaskutUriObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      map(asiakas => {
        if (asiakas?.avain) {
          return this._laskuUriService.getToistuvatLaskutCollectionUri(asiakas.avain)
        }
        return ''
      }),
    )

    this.toistuvatLaskutEncodedUriObservable = this.toistuvatLaskutUriObservable.pipe(map(uri => this._debugService.createFirestoreLinkLemonaid(uri)))

    this._asiakasService.nykyinenAsiakasObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(asiakas => {
      if (asiakas) {
        this.asiakas = asiakas
        this.haeToistuvatLaskut()
      }
    })
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  async haeToistuvatLaskut() {
    const toistuvaLaskuUri = this._laskuUriService.getToistuvatLaskutCollectionUri(this.asiakas.avain)

    this._firebaseLemonaid
      .firestoreCollection<ToistuvaLasku>(toistuvaLaskuUri)
      .listenSnapshots()
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe({
        next: snapshots => {
          const rivit: ToistuvaLaskuRivi[] = []
          for (const snapshot of snapshots) {
            const toistuva = snapshot.data() as ToistuvaLasku
            if (!toistuva.poistettu) {
              rivit.push({
                ...toistuva,
                avain: snapshot.id,
                asiakasNimi: toistuva.lasku.asiakas?.nimi || '',
                summa: toistuva.lasku.summa || 0,
                seuraavaLahetysPvmDate: this._dateService.numberToDate(toistuva.seuraavaLahetysPvm),
                lahetysFrekvenssi: this.muotoileLahetysFrekvenssi(toistuva.toisto.toistoTyyppi),
                ensimmainenLaskuPvm: this._dateService.numberToDate(toistuva.toisto.ensimmainenLahetyspvm),
                viimeinenLaskuPvm: this._laskuLahetyksenToistoService.annaViimeinenLaskupvmJosLaskusarjassa(toistuva.toisto, toistuva.lahetyksia),
                yhteensaLahetyksia: this._laskuLahetyksenToistoService.laskeLahetyksienKokonaismaara(toistuva.toisto),
              })
            }
          }

          this.dataSource.data = rivit
          this.lataa.set(false)
        },
      })
  }

  katseleLaskua(rivi: ToistuvaLaskuRivi) {
    this._router.navigate(['/asiakkaat', this.asiakas.avain, 'laskut', 'selaa', rivi.lasku.avain, rivi.lasku.avain], {
      queryParams: { paluu: 'toistuvat' },
    })
  }

  katseleLahetettyaLaskua(rivi: ToistuvaLaskuRivi, laskuAvain: string) {
    this._router.navigate(['/asiakkaat', this.asiakas.avain, 'laskut', 'selaa', laskuAvain, laskuAvain], {
      queryParams: { paluu: 'toistuvat' },
    })
  }

  async toggleRow(rivi: ToistuvaLaskuRivi) {
    if (this.expandedElement === rivi) {
      this.expandedElement = null
      return
    }

    this.expandedElement = rivi

    if (!rivi.lahetetytLaskut) {
      rivi.ladataanLahetettyjaTietoja = true
      this.dataSource.data = [...this.dataSource.data]

      try {
        const laskutUri = this._laskuUriService.getCustomerLaskutCollectionUri(this.asiakas.avain)

        const laskut = await this._firebaseLemonaid.firestoreCollection<Lasku>(laskutUri).where('toistoasetustenAvain', '==', rivi.avain).get()

        rivi.lahetetytLaskut = laskut
          .map(lasku => ({
            avain: lasku.avain,
            nro: lasku.nro,
            p: lasku.p,
            erap: lasku.erap,
            summa: lasku.summa,
          }))
          .sort((a, b) => b.p - a.p)
      } catch (error) {
        console.error('Virhe haettaessa lähetettyjä laskuja:', error)
        rivi.lahetetytLaskut = []
      } finally {
        rivi.ladataanLahetettyjaTietoja = false
        this.dataSource.data = [...this.dataSource.data]
        this._changeDetectorRef.markForCheck()
      }
    }
  }

  private muotoileLahetysFrekvenssi(toistoTyyppi: LaskuToistoTyyppi): string {
    switch (toistoTyyppi) {
      case 'kerran':
        return this._translationService.lokalisoi('toistuvat.frekvenssi.kerran')
      case 'kk':
        return this._translationService.lokalisoi('toistuvat.frekvenssi.kuukausittain')
      case 'kk3':
        return this._translationService.lokalisoi('toistuvat.frekvenssi.3kk-valein')
      case 'kk6':
        return this._translationService.lokalisoi('toistuvat.frekvenssi.6kk-valein')
      case 'vuosi':
        return this._translationService.lokalisoi('toistuvat.frekvenssi.vuosittain')
      default:
        return toistoTyyppi
    }
  }
}
